// opted against using react-bootstrap v3 (not maintained) pagination because of issues with a11y

import React, { useState, useEffect, useRef } from 'react';
import { isBrowser } from 'ws-scripts/modules/environment';
import logger from 'ws-scripts/modules/logger';
import { ErrorBoundary } from 'wsm-error-boundary';
import { setNewRelicCustomAttribute } from '@ddc/new-relic';
import { useLabels } from 'wsm-common-data';
import {
	getCurrentPageIndex,
	getPaginationItems,
	getTotalPages
} from 'wsm-srp-utilities';
import { isPagingNeeded } from '../utilities/paging';
import PaginationItem from '../components/PaginationItem';
import PaginationNext from '../components/PaginationNext';
import PaginationPrevious from '../components/PaginationPrevious';
import PaginationEllipsis from '../components/PaginationEllipsis';
import PagingContainer from '../components/PagingContainer';
import { shouldHidePagingBecauseOfOffSiteData } from '../utilities/offSiteUtils';
import { useInventory } from '../utilities/waitForInvData';

const getOffSiteSessionStorage = () => {
	return (
		(typeof sessionStorage !== 'undefined' &&
			sessionStorage.getItem('showOffSite') &&
			true) ||
		false
	);
};

const Widget = () => {
	const [showOffSite, setShowOffSite] = useState(getOffSiteSessionStorage());
	const { isLoading, isError, paging } = useInventory();
	const labels = useLabels();
	const [pageAnnouncement, setPageAnnouncement] = useState('');
	// Tracks previous page number so we only announce page changes,
	// not the initial render. Starts null to distinguish "no page yet" from page 0.
	const prevPageRef = useRef(null);

	useEffect(() => {
		if (isBrowser && window?.DDC?.pubsub) {
			window.DDC.pubsub.subscribe(
				'ws-inv-listing/showOffSiteInventory',
				() => {
					setShowOffSite(true);
				}
			);
		}
	}, []);

	useEffect(() => {
		if (!paging || isLoading || isError) {
			return;
		}
		const currentPage = getCurrentPageIndex(
			paging.pageStart,
			paging.pageSize
		);
		// Only announce after the user navigates, not on initial render
		if (
			prevPageRef.current !== null &&
			prevPageRef.current !== currentPage
		) {
			const totalPages = getTotalPages(
				paging.totalCount,
				paging.pageSize
			);
			const template = labels.get('PAGE_X_OF_Y');
			setPageAnnouncement(
				template
					.replace('{0}', currentPage + 1)
					.replace('{1}', totalPages)
			);
		}
		prevPageRef.current = currentPage;
	}, [paging, isLoading, isError, labels]);

	if (isLoading || isError) {
		return null;
	}

	const { offSiteData } = window?.DDC?.InvData?.getFacets() || {};

	// case for when paging shouldn't render
	if (
		!isPagingNeeded(paging) ||
		shouldHidePagingBecauseOfOffSiteData(offSiteData, paging, showOffSite)
	) {
		return null;
	}

	const currentPage = getCurrentPageIndex(paging.pageStart, paging.pageSize);
	const items = getPaginationItems(currentPage, paging);
	const pages = items.map((item) => {
		if (item.type === 'ellipsis') {
			return <PaginationEllipsis key={item.key} />;
		}
		return (
			<PaginationItem
				key={item.key}
				currentPage={currentPage}
				pageIndex={item.pageIndex}
				pageNumber={item.pageNumber}
				pageSize={paging.pageSize}
			/>
		);
	});

	return (
		<ErrorBoundary
			errorHandler={(error, errorInfo) => {
				setNewRelicCustomAttribute(
					'SRP ERROR',
					`ws-inv-paging error boundary.\n${error}`
				);
				const newError = new Error(
					`ws-inv-paging error boundary.\n${error}`
				);
				newError.originalError = error;
				newError.originalStackTrace = errorInfo.componentStack;
				logger.error(`${newError}\n${newError.originalStackTrace}`);
			}}
		>
			<PagingContainer>
				<nav aria-label={labels.get('MATCHING_RESULTS_PAGES')}>
					<ul className="pagination">
						<PaginationPrevious paging={paging} />
						{pages}
						<PaginationNext paging={paging} />
					</ul>
				</nav>
				<span className="sr-only" role="status" aria-live="polite">
					{pageAnnouncement}
				</span>
			</PagingContainer>
		</ErrorBoundary>
	);
};

export default Widget;
